/**
 * View for the global search modal overlay.
 *
 * Renders a size="lg" modal matching the Add Player modal structure:
 * ModalHeader with title, then the shared search body (SearchToolbar,
 * ActiveFilterChips, InlineFilterPanel and either the curated sections or
 * PlayerSearchResultsList).
 *
 * SE-7242 moved that body into `GlobalSearchBody` so quick capture can host
 * the same search inside `MobileSheetChrome` on native, where nesting a Modal
 * would mean two backdrops and two scrollers. This file's rendered output is
 * unchanged by that move — it is the same tree with the middle of it named.
 * @module features/global-search/components/GlobalSearchModal/GlobalSearchModalView
 */

import React, { memo } from "react";

import GlobalSearchBody from "@/features/global-search/components/organisms/GlobalSearchBody";
import type { GlobalSearchBodyViewProps } from "@/features/global-search/components/organisms/GlobalSearchBody";

import { Stack } from "@/components/atoms/Stack";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Text } from "@/components/atoms/Text";

/**
 * Props for GlobalSearchModalView.
 *
 * Everything the body needs, plus the chrome this file owns (the title).
 */
interface GlobalSearchModalViewProps extends GlobalSearchBodyViewProps {
  /** Modal title — defaults to "Search Player" */
  readonly title?: string;
}

/**
 * Global search modal overlay matching the Add Player modal structure.
 * Uses size="lg", ModalHeader with title, and the shared search body.
 * @param root0 - Component props
 * @param root0.title - Modal heading text
 * @param root0.isOpen - Whether the modal is open
 * @param root0.onClose - Close handler
 */
const GlobalSearchModalView = ({
  title = "Search Player",
  isOpen,
  onClose,
  ...bodyProps
}: GlobalSearchModalViewProps) => (
  <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick size="lg">
    <ModalBackdrop />
    <ModalContent className="web:max-w-5xl h-4/5 p-0">
      <ModalHeader className="px-6 pb-3 pt-5">
        <Stack direction="horizontal" className="flex-1 items-center gap-3">
          <Text variant="display-sm" className="flex-1 text-content-primary">
            {title}
          </Text>
        </Stack>
      </ModalHeader>

      <ModalBody
        className="!my-0 min-h-0 flex-1 grow p-0"
        scrollEnabled={false}
        contentContainerStyle={{ flex: 1 }}
      >
        <GlobalSearchBody {...bodyProps} isOpen={isOpen} onClose={onClose} />
      </ModalBody>
    </ModalContent>
  </Modal>
);

GlobalSearchModalView.displayName = "GlobalSearchModalView";

export default memo(GlobalSearchModalView);
